import React, { useState, useRef, useEffect, useContext, useMemo } from "react";

import { useHistory } from "react-router-dom";
import useSound from "use-sound";

import Popover from "@material-ui/core/Popover";
import IconButton from "@material-ui/core/IconButton";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import Badge from "@material-ui/core/Badge";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Checkbox from "@material-ui/core/Checkbox";
import Tooltip from "@material-ui/core/Tooltip";

import TicketListItem from "../TicketListItem";

import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { cleanupComponent, getSocket, onSocket } from "../../services/socket";
import { Can } from "../Can";

import NotificationBellIcon from "../../assets/icon/NotificationBellIcon";
import alertSound from "../../assets/sound.mp3";
import { NOTIFY_PREF_KEYS, getNotifyPref, setNotifyPref } from "../../utils/notificationPrefs";

const useStyles = makeStyles((theme) => ({
  tabContainer: {
    overflowY: "auto",
    maxHeight: 350,
    ...theme.scrollbarStyles,
  },
  popoverPaper: {
    width: "100%",
    maxWidth: 350,
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(1),
    borderRadius: 8,
    [theme.breakpoints.down("sm")]: {
      maxWidth: 270,
    },
  },
  noShadow: {
    boxShadow: "none !important",
  },
  titleContainer: {
    backgroundColor: theme.palette.tabHeaderBackground,
    padding: theme.spacing(2),
  },
  titleRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
  },
  title: {
    color: theme.palette.text.secondary,
    fontSize: '1.1rem',
    fontWeight: 500
  },
  optionsRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    paddingTop: theme.spacing(1),
  },
  badge: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  }
}));

const NotificationsPopOver = () => {
  const classes = useStyles();

  const history = useHistory();
  const { user } = useContext(AuthContext);
  const ticketIdUrl = +history.location.pathname?.split("/")[2];

  // 🔔 Estado de preferencias de notificación (refleja localStorage y se
  // actualiza al alternar los checks en el header del popover).
  const [notifyPrefs, setNotifyPrefs] = useState({
    notifyPending: getNotifyPref(NOTIFY_PREF_KEYS.notifyPending, true),
    onlyAssigned: getNotifyPref(NOTIFY_PREF_KEYS.onlyAssigned, false),
  });
  const ticketIdRef = useRef(ticketIdUrl);
  const anchorEl = useRef();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [invisible, setInvisible] = useState(true);
  const [, setDesktopNotifications] = useState([]);
  const componentId = useMemo(() => {
      const timestamp = Date.now();
      const random = Math.random().toString(36).substr(2, 9);
      return `notificationsPopOver-${timestamp}-${random}`;
  }, []);

  // Verificar que user esté definido antes de continuar
  if (!user || !user.id) {
    return (
      <IconButton
        aria-label="Mostrar Notificaciones"
        variant="contained"
        disabled
      >
        <Badge
          badgeContent={0}
          classes={{ badge: classes.badge }}
          invisible={true}
          max={99}
        >
          <NotificationBellIcon />
        </Badge>
      </IconButton>
    );
  }

  const [play] = useSound(alertSound);
  const soundAlertRef = useRef();

  const historyRef = useRef(history);

  useEffect(() => {
    soundAlertRef.current = play;

    if (!("Notification" in window)) {
      console.log("This browser doesn't support notifications");
    } else {
      Notification.requestPermission();
    }
  }, [play]);

  useEffect(() => {
    ticketIdRef.current = ticketIdUrl;
  }, [ticketIdUrl]);

  // 🧱 Normaliza un record de user:notification a un item del popover
  const buildNotificationItem = (record) => {
    const ticket = record?.ticket || {};
    const ticketId = ticket.id ?? record.ticketId;
    const contact = ticket.contact || {};
    const userAssigned = ticket.user || {};
    const queue = ticket.queue || {};
    const whatsapp = ticket.whatsapp || {};

    const body = record.body || ticket.lastMessage || "";
    const counter =
      Number.isFinite(record.unreadMessages) ? record.unreadMessages : 1;

    return {
      id: ticketId,
      ticketId,
      ticketUuid: ticket.uuid,
      type: record.type || "message:new",
      title: `${contact.name || ""}${queue.name ? ` - ${queue.name}` : ""}`.trim(),
      text: body,
      body,
      fromMe: !!record.fromMe,
      mediaType: record.mediaType || "text",
      messageId: record.messageId,
      unreadMessages: counter,
      unreadCount: counter,
      ticket,
      contact,
      user: userAssigned,
      queue,
      whatsapp,
      tags: ticket.tags || [],
      createdAt: record.createdAt || new Date().toISOString(),
    };
  };

  // 🔁 Une el evento entrante con su item previo (acumula contador y actualiza payload)
  const mergeNotification = (prevState, record) => {
    const ticket = record?.ticket || {};
    const ticketId = ticket.id ?? record.ticketId;
    const baseItem = buildNotificationItem(record);

    const existingIndex = prevState.findIndex(
      (n) => (n.ticketId ?? n.ticket?.id) === ticketId
    );

    if (existingIndex === -1) {
      return [baseItem, ...prevState];
    }

    const previous = prevState[existingIndex];
    const merged = {
      ...previous,
      ...baseItem,
      unreadCount: (previous.unreadCount || previous.unreadMessages || 1) + 1,
      unreadMessages: (previous.unreadMessages || previous.unreadCount || 1) + 1,
      text: baseItem.text || previous.text,
      body: baseItem.body || previous.body,
      ticket: { ...previous.ticket, ...ticket },
      contact: { ...previous.contact, ...(ticket.contact || {}) },
      user: { ...previous.user, ...(ticket.user || {}) },
      queue: { ...previous.queue, ...(ticket.queue || {}) },
      whatsapp: { ...previous.whatsapp, ...(ticket.whatsapp || {}) },
      tags: ticket.tags || previous.tags || [],
      createdAt: baseItem.createdAt,
    };

    const filtered = prevState.filter((_, idx) => idx !== existingIndex);
    return [merged, ...filtered];
  };

  // 🔔 Notificación nativa del navegador
  const showDesktopNotification = (item) => {
    if (typeof Notification === "undefined" || Notification.permission !== "granted") return;
    const title = item.title || item.contact?.name || "Nueva notificación";
    const notification = new Notification(title, {
      body: item.body || item.text || "",
      tag: String(item.ticketId || item.id),
      icon: item.contact?.profilePicUrl || "/logo192.png",
      renotify: true,
    });

    notification.onclick = (e) => {
      e.preventDefault();
      window.focus();
      if (item.ticketUuid) {
        historyRef.current.push(`/tickets/${item.ticketUuid}`);
      }
    };

    setDesktopNotifications((prevState) => {
      const existingIndex = prevState.findIndex(
        (n) => n.tag === notification.tag
      );
      if (existingIndex !== -1) {
        const newState = [...prevState];
        newState[existingIndex] = notification;
        return newState;
      }
      return [notification, ...prevState];
    });
  };

  // 🔍 Determina si el usuario está viendo el ticket del record
  const isViewingTicket = (record) => {
    const ticketUuid = record?.ticket?.uuid;
    if (!ticketUuid) return false;
    const currentPath = historyRef.current?.location?.pathname || "";
    const currentUuid = currentPath.split("/")[2];
    return currentUuid === ticketUuid;
  };

  // 👁️ La app está en primer plano: pestaña visible y ventana con foco.
  // Cubrir tab oculta, minimizada u otra ventana/app activa.
  const isAppInForeground = () => {
    if (typeof document === "undefined") return true;
    return document.visibilityState === "visible" && document.hasFocus();
  };

  // 🎬 Despachadores por tipo de evento
  const handleMessageNew = (data) => {
    const record = data?.record;
    if (!record) return;

    // 🛡️ Semántica de los toggles:
    //   - "Pendientes" controla notificaciones de tickets NO activos
    //     (pending/closed). NO afecta a tickets en Activos (open),
    //     porque esos son trabajo activo del usuario y deben notificar.
    //   - "Solo míos" filtra notificaciones de tickets en Activos (open).
    //     Sólo aplica a tickets abiertos; pending/closed nunca tienen
    //     userId asignado, así que el filtro no aplicaría ahí.
    const ticketStatus = record.ticket?.status;
    const isActiveTicket = ticketStatus === "open";

    if (isActiveTicket) {
      // 🎯 Ticket en Activos (open):
      //   "Pendientes" NO bloquea. Sólo se filtra por "Solo míos".
      const onlyAssigned = getNotifyPref(NOTIFY_PREF_KEYS.onlyAssigned, false);
      if (onlyAssigned) {
        const ticketUserId = record.ticket?.userId;
        if (ticketUserId == null || ticketUserId !== user?.id) return;
      }
    } else {
      // 💤 Ticket no activo (pending/closed):
      //   Sólo el toggle "Pendientes" decide si pasa.
      const notifyPending = getNotifyPref(NOTIFY_PREF_KEYS.notifyPending, true);
      if (!notifyPending) return;
    }

    const recordTicketUuid = record.ticket?.uuid;
    const viewingTicket = isViewingTicket(record);
    const appInForeground = isAppInForeground();

    // Solo se omite si el usuario está viendo ESTE chat Y la app está presente.
    // Si cambió de tab, minimizó o perdió el foco, sí se notifica.
    if (viewingTicket && appInForeground) return;

    const item = buildNotificationItem(record);
    setNotifications((prevState) => mergeNotification(prevState, record));

    // 🔔 App NO presente: sonido + notificación nativa.
    // 🔕 App presente (pero en otro ticket): solo sonido.
    if (!appInForeground) {
      soundAlertRef.current?.();
      showDesktopNotification(item);
    } else {
      soundAlertRef.current?.();
    }

    console.log("user:notification handling", {
      recordTicketUuid,
      ticketStatus,
      isActiveTicket,
      appInForeground,
      viewingTicket,
    });
  };

  // 🧭 Switch principal por tipo de record
  const handleUserNotification = (data) => {
    if (data?.action !== "create" || !data?.record) return;
    switch (data.record.type) {
      case "message:new":
        handleMessageNew(data);
        break;
      // case "ticket:update":
      //   handleTicketUpdate(data);
      //   break;
      // case "ticket:delete":
      //   handleTicketDelete(data);
      //   break;
      // case "ticket:reset_unread":
      //   handleResetUnread(data);
      //   break;
      default:
        // Tipo desconocido: intentar tratarlo como message:new para no perder feedback
        // handleMessageNew(data);
        break;
    }
  };

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = getSocket(companyId);

    // ============================================================
    // 🔌 LISTENERS LEGACY (deshabilitados tras unificar en user:notification)
    // Se conservan comentados como referencia durante la migración.
    // ------------------------------------------------------------
    // onSocket(`company:ticket`, (data) => { ... }, componentId);
    // onSocket(`company:appMessage`, (data) => { ... }, componentId);
    // ============================================================

    onSocket(`user:notification`, handleUserNotification, componentId);

    return () => {
      cleanupComponent(componentId);
    };
  }, [user]);

  const handleClick = () => {
    setIsOpen((prevState) => !prevState);
  };

  const handleClickAway = () => {
    setIsOpen(false);
  };

  const NotificationTicket = ({ children, onClick }) => {
    return <div onClick={onClick}>{children}</div>;
  };

  const handleDismissNotification = (item) => {
    const ticketId = item.ticketId ?? item.id ?? item.ticket?.id;
    if (!ticketId) return;
    setNotifications((prevState) =>
      prevState.filter((n) => (n.ticketId ?? n.ticket?.id) !== ticketId)
    );
  };

  // Render plano de notificaciones (un solo formato)
  const allNotifications = useMemo(() => {
    return notifications
      .map((item) => {
        const ticketId = item.ticketId ?? item.ticket?.id ?? item.id;
        const unreadMessages = item.unreadCount || item.unreadMessages || 1;
        const body = item.body || item.text || item.ticket?.lastMessage || "";
        const contact = item.contact || item.ticket?.contact || {};

        const ticket = {
          ...(item.ticket || {}),
          id: ticketId,
          contact,
          unreadMessages,
          lastMessage: body,
          updatedAt: item.createdAt || item.ticket?.updatedAt,
        };

        return {
          ...item,
          id: ticketId,
          body,
          ticket,
          contact,
          unreadMessages,
        };
      })
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }, [notifications]);

  // Actualizar la visibilidad del badge basado en la cantidad de notificaciones
  useEffect(() => {
    setInvisible(allNotifications.length === 0);
  }, [allNotifications]);

  return (
    <>
      <IconButton
        onClick={handleClick}
        ref={anchorEl}
        aria-label="Mostrar Notificaciones"
        variant="contained"
      >
        <Badge
          badgeContent={allNotifications.length}
          classes={{ badge: classes.badge }}
          invisible={invisible}
          max={99}
        >
          <NotificationBellIcon />
        </Badge>
      </IconButton>
      <Popover
        disableScrollLock
        open={isOpen}
        anchorEl={anchorEl.current}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        classes={{ paper: classes.popoverPaper }}
        onClose={handleClickAway}
      >
        <div className={classes.titleContainer}>
          <div className={classes.titleRow}>
            <Typography className={classes.title}>
              Notificaciones
            </Typography>
          </div>
          <div className={classes.optionsRow}>
            <Tooltip title="Activar notificaciones de mensajes de chats pendientes">
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={notifyPrefs.notifyPending}
                    onChange={(e) => {
                      const value = e.target.checked;
                      setNotifyPref(NOTIFY_PREF_KEYS.notifyPending, value);
                      setNotifyPrefs((prev) => ({ ...prev, notifyPending: value }));
                    }}
                    color="primary"
                  />
                }
                label="Pendientes"
              />
            </Tooltip>
            <Can
              role={user?.profile}
              perform="user-modal:notificationOptions"
              yes={() => (
                <Tooltip title="Solo notificar chats asignados a mí">
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={notifyPrefs.onlyAssigned}
                        onChange={(e) => {
                          const value = e.target.checked;
                          setNotifyPref(NOTIFY_PREF_KEYS.onlyAssigned, value);
                          setNotifyPrefs((prev) => ({ ...prev, onlyAssigned: value }));
                        }}
                        color="primary"
                      />
                    }
                    label="Solo míos"
                  />
                </Tooltip>
              )}
            />
          </div>
        </div>
        <List dense className={classes.tabContainer}>
          {allNotifications.length === 0 ? (
            <ListItem>
              <ListItemText>{i18n.t("notifications.noTickets")}</ListItemText>
            </ListItem>
          ) : (
            allNotifications.map((item) => (
              <NotificationTicket key={item.id} onClick={() => handleDismissNotification(item)}>
                <TicketListItem ticket={item} />
              </NotificationTicket>
            ))
          )}
        </List>
      </Popover>
    </>
  );
};

export default NotificationsPopOver;
